import { get, post, put } from 'api/http-client';
import { createOwnersAPI } from 'api/ownersApi';
import { createRevenueAPI } from 'api/revenueApi';
import { createRASAPI } from 'api/rasApi';
import {
  USE_ACCOUNTING_FLOW,
  USE_ACCOUNTING_NA_REVENUE,
  OWN_FRACTIONAL_OWNERSHIP,
} from 'constants/featureToggle';
import {
  getFromTo,
  getMonthYearKey,
  getPrevMonthKey,
  getDateFromKey,
  getNextMonthKey,
} from 'utils/common';
import { whoami } from 'user/user.services';

import { formatDatesWithoutUTC } from './property.utils';

const getOwnerRevenue = (params) =>
  createRevenueAPI().get('/analytics/owner', { params });
const getRASAccommodationFare = (params) =>
  createRASAPI().get('/accommodation-fare', { params });
const getRASAverageNightlyRate = (params) =>
  createRASAPI().get('/average-nightly-rate', { params });
const getRASRevPal = (params) => createRASAPI().get('/revpal', { params });
const getRASOwnersRevenue = (params) =>
  createRASAPI().get('/owners-revenue', { params });

const listingsFields = [
  '_id',
  'title',
  'bedrooms',
  'type',
  'propertyType',
  'address.full',
  'picture.regular',
  'picture.thumbnail',
  'active',
  'nickname',
  'defaultCheckInTime',
  'defaultCheckOutTime',
  'complexId',
  'mtl',
];

export const getListings = (data = {}) => {
  const params = {
    fields: listingsFields.join(' '),
    ...data,
  };

  return createOwnersAPI()
    .get('/me/listings', { params })
    .then((response) => response.data);
};

export const getCalendarFromTo = async (listingId, from, to, options) => {
  const fieldsList = [
    'date',
    'accountId',
    'listingId',
    'guest',
    'status',
    'note',
    'price',
    'currency',
    'listing.prices.currency',
    'reservationId',
    'reservation',
    'blocks',
    'ownerReservation',
    'ownerReservationId',
  ];

  const ownerId = whoami().userId;
  const params = {
    from,
    to,
    ownerId,
    fields: fieldsList.join(' '),
  };

  const isAccountingFTEnabled =
    window.featureToggle?.isEnabled(USE_ACCOUNTING_FLOW);
  const isAccountingNARevenueEnabled = window.featureToggle?.isEnabled(
    USE_ACCOUNTING_NA_REVENUE
  );
  const isAccountingFractionalOwnershipEnabled =
    window.featureToggle?.isEnabled(OWN_FRACTIONAL_OWNERSHIP);

  let calendarPromise;

  if (
    isAccountingFTEnabled &&
    (isAccountingNARevenueEnabled || options.ownersPortalNetRentalIncomeEnabled)
  ) {
    calendarPromise = createRevenueAPI()
      .get(`/analytics/listings/${listingId}/calendar`, {
        params,
      })
      .then((response) => response.data);
  } else {
    calendarPromise = get(`/v2/listings/${listingId}/calendar`, params);
  }

  if (!isAccountingFTEnabled || !isAccountingFractionalOwnershipEnabled) {
    return calendarPromise;
  }

  const calendarData = await calendarPromise;

  const reservationIds = [
    ...new Set(calendarData.map((day) => day.reservationId).filter(Boolean)),
  ];

  if (!reservationIds.length) {
    return calendarData;
  }

  const { data: fractionalsPerReservationData } = await createRevenueAPI().get(
    `/analytics/owner/${ownerId}`,
    {
      params: {
        reservationIds: reservationIds.join(','),
      },
    }
  );

  fractionalsPerReservationData.forEach((item) => {
    calendarData.forEach((day) => {
      if (day.reservationId === item.reservationId) {
        const newReservation = {
          ...day.reservation,
          accounting: {
            ...day.reservation?.accounting,
            analytics: {
              ...day.reservation?.accounting?.analytics,
              fractionals: {
                reservationBased: item.reservationBased,
                accountBased: item.accountBased,
              },
            },
          },
        };

        // eslint-disable-next-line no-param-reassign
        day.reservation = newReservation;
      }
    });
  });

  return calendarData;
};

export const getCalendarMonthYear = (listingId, month, year, options) => {
  const { from, to } = getFromTo(month, year);
  return getCalendarFromTo(listingId, from, to, options);
};

export const getListingMiniData = (listingId, month, year) => {
  const useRas = !window.featureToggle?.isEnabled(USE_ACCOUNTING_FLOW);
  const params = {
    listingIds: listingId,
    ownerId: whoami().userId,
    ...getFromTo(month, year),
  };

  const ownerRevenuePromise = getOwnerRevenue({
    ...params,
    view: 'table',
    confirmedOnly: true,
  })
    .then((response) => {
      const result = response.data;
      const listing = result.tableData.find((x) => x._id === listingId);

      if (!listing) {
        return {};
      }

      let revenue;
      let bookedNights;

      const {
        analytics,
        revpal,
        occupancy,
        average_nightly_rate: avgNightlyRate,
      } = listing;

      if (analytics) {
        revenue = analytics.find((x) => x.type === 'OWNER_REVENUE') || {};
        bookedNights = analytics.find((x) => x.type === 'OCCUPANCY');
      }

      return {
        revpal,
        revenue,
        occupancy,
        bookedNights,
        avgNightlyRate,
        revenueByReservationStatus: revenue.byReservationStatus,
      };
    })
    .catch(() => {
      return {};
    });

  const promises = [ownerRevenuePromise];

  if (useRas) {
    const rasParams = {
      listing_id: listingId,
      ...getFromTo(month, year),
    };

    const rasAccommodationFarePromise = getRASAccommodationFare(rasParams)
      .then((response) => response.data?.data?.[0]?.accommodationFare)
      .catch(() => ({}));

    const rasAverageNightlyRatePromise = getRASAverageNightlyRate(rasParams)
      .then((response) => response.data?.data?.[0]?.avgNightlyRate)
      .catch(() => ({}));

    const rasRevPalPromise = getRASRevPal(rasParams)
      .then((response) => response.data?.data?.[0]?.revpal)
      .catch(() => ({}));

    const rasOwnersRevenuePromise = getRASOwnersRevenue(rasParams)
      .then((response) => response.data?.data?.[0]?.ownersRevenue)
      .catch(() => ({}));

    promises.push(
      rasAccommodationFarePromise,
      rasAverageNightlyRatePromise,
      rasRevPalPromise,
      rasOwnersRevenuePromise
    );
  }

  return Promise.all(promises)
    .then(
      ([
        ownerRevenue,
        rasAccommodationFare,
        rasAverageNightlyRate,
        rasRevPal,
        rasOwnersRevenue,
      ]) => {
        if (useRas) {
          return {
            bookedNights: ownerRevenue.bookedNights,
            occupancy: ownerRevenue.occupancy,
            accommodationFare: rasAccommodationFare || undefined,
            avgNightlyRate: rasAverageNightlyRate || undefined,
            revpal: rasRevPal || undefined,
            revenue: {
              type: 'OWNER_REVENUE',
              total: rasOwnersRevenue || undefined,
              listingId,
              byReservationStatus: {},
            },
            revenueByReservationStatus: {},
          };
        }

        return ownerRevenue;
      }
    )
    .catch(() => {
      return {};
    });
};

export const fetchCalendarData = (
  listing,
  month,
  year,
  { getCalendarMonthYear: _getCalendarMonthYear, getMiniData },
  options,
  forceLoad = false
) => {
  const listingId = listing._id;
  const monthKey = getMonthYearKey(month, year);
  const calendar = listing.calendar || {};
  if (!calendar[monthKey]) {
    _getCalendarMonthYear(listingId, month, year, options);
  }

  const prevMonthKey = getPrevMonthKey(monthKey);
  if (!calendar[prevMonthKey] || forceLoad) {
    const prevMonth = getDateFromKey(prevMonthKey);
    _getCalendarMonthYear(
      listingId,
      prevMonth.getMonth(),
      prevMonth.getFullYear(),
      options
    );
  }

  const prevPrevMonthKey = getPrevMonthKey(prevMonthKey);
  if (!calendar[prevPrevMonthKey] || forceLoad) {
    const prevPrevMonth = getDateFromKey(prevPrevMonthKey);
    _getCalendarMonthYear(
      listingId,
      prevPrevMonth.getMonth(),
      prevPrevMonth.getFullYear(),
      options
    );
  }

  const nextMonthKey = getNextMonthKey(monthKey);
  if (!calendar[nextMonthKey] || forceLoad) {
    const nextMonth = getDateFromKey(nextMonthKey);
    _getCalendarMonthYear(
      listingId,
      nextMonth.getMonth(),
      nextMonth.getFullYear(),
      options
    );
  }

  if (!listing.miniData || !listing.miniData[monthKey] || forceLoad) {
    getMiniData(listingId, month, year);
  }
};

export const addReservation = (checkIn, checkOut, listingId, note) =>
  post('/v2/owners-reservations', {
    ...formatDatesWithoutUTC(checkIn, checkOut),
    listingId,
    note,
  });

export const updateReservation = (
  ownerReservationId,
  checkIn,
  checkOut,
  note
) =>
  put(`/v2/owners-reservations/${ownerReservationId}`, {
    ...formatDatesWithoutUTC(checkIn, checkOut),
    note,
  });

export const cancelReservation = (ownerReservationId) =>
  post(`/v2/owners-reservations/${ownerReservationId}`, {});
